/**
 * utils/session.ts
 *
 * SINGLE SOURCE OF TRUTH for session phase detection and market-open logic.
 *
 * Replaces scattered implementations in:
 *   - ai/contextBuilder.ts   (getSessionPhase taking Candle[])
 *   - research/researchSnapshot.ts (private getSessionPhase())
 *   - hooks/useMarketData.ts (isMarketOpen())
 *   - engines/regimeEngine.ts (inline hour comparisons)
 *
 * All session logic flows through this module.
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export type SessionPhase =
  | 'pre-market'
  | 'opening'
  | 'first-hour'
  | 'mid-morning'
  | 'midday'
  | 'afternoon'
  | 'power-hour'
  | 'after-hours';

// ─── Core functions ───────────────────────────────────────────────────────────

/** Returns the current session phase based on wall-clock time (US Eastern). */
export function getSessionPhase(): SessionPhase {
  const now  = new Date();
  // Approximate ET offset — production deployments should use proper timezone library
  const hour = now.getHours() + now.getMinutes() / 60;

  if (hour < 9.5)          return 'pre-market';
  if (hour < 9.75)         return 'opening';
  if (hour < 10.5)         return 'first-hour';
  if (hour < 11.5)         return 'mid-morning';
  if (hour < 13.5)         return 'midday';
  if (hour < 15.0)         return 'afternoon';
  if (hour < 16.0)         return 'power-hour';
  return 'after-hours';
}

/** Returns true if the current time falls within regular market hours (9:30–16:00 ET). */
export function isMarketHours(): boolean {
  const now  = new Date();
  const hour = now.getHours() + now.getMinutes() / 60;
  const dow  = now.getDay(); // 0=Sun, 6=Sat
  if (dow === 0 || dow === 6) return false;
  return hour >= 9.5 && hour < 16.0;
}

/**
 * Derives session phase from the last candle's timestamp.
 * Preferred for historical/replay data where wall-clock time is not appropriate.
 */
export function getSessionPhaseFromCandle(candleTs: number): SessionPhase {
  const d    = new Date(candleTs);
  const hour = d.getHours() + d.getMinutes() / 60;

  if (hour < 9.5)  return 'pre-market';
  if (hour < 9.75) return 'opening';
  if (hour < 10.5) return 'first-hour';
  if (hour < 11.5) return 'mid-morning';
  if (hour < 13.5) return 'midday';
  if (hour < 15.0) return 'afternoon';
  if (hour < 16.0) return 'power-hour';
  return 'after-hours';
}

/** Human-readable label for a session phase. */
export function getSessionLabel(phase: SessionPhase): string {
  const labels: Record<SessionPhase, string> = {
    'pre-market':  'Pre-Market',
    'opening':     'Opening Range',
    'first-hour':  'First Hour',
    'mid-morning': 'Mid Morning',
    'midday':      'Midday',
    'afternoon':   'Afternoon',
    'power-hour':  'Power Hour',
    'after-hours': 'After Hours',
  };
  return labels[phase];
}

/** Returns true if the session phase is a high-activity window. */
export function isHighActivityPhase(phase: SessionPhase): boolean {
  return phase === 'opening' || phase === 'first-hour' || phase === 'power-hour';
}
